import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useUserRoles } from '@/hooks/useUserRoles';
import { useQuotes } from '@/hooks/useQuotes';
import { useRealtimeQuotes } from '@/hooks/bookings/useRealtimeQuotes';
import { supabase } from '@/integrations/supabase/client';
import { BookingDetails as BookingDetailsType } from '@/hooks/bookings/types';
import { BookingDetailsHeader } from '@/components/bookings/booking-details/BookingDetailsHeader';
import { BookingDetailsTabs } from '@/components/bookings/booking-details/BookingDetailsTabs';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BookingDetails = () => {
  const { bookingId } = useParams<{ bookingId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { roles, isLoading: rolesLoading } = useUserRoles();
  
  const [booking, setBooking] = useState<BookingDetailsType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check URL hash to determine default tab
  const defaultTab = window.location.hash === '#quote' ? 'quotes' : 'details';

  const quotesQuery = useQuotes(bookingId || '');
  const isAdmin = roles.isAdmin;
  const isBookingOwner = booking?.user_id === user?.id;

  // Setup realtime subscription for quotes
  useRealtimeQuotes({
    bookingId: bookingId || '',
    enabled: !!bookingId
  });

  // Fetch booking details
  const fetchBooking = useCallback(async () => {
    if (!bookingId || rolesLoading) return;

    setIsLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from('bookings')
        .select(`
          *,
          profiles!bookings_user_id_fkey (
            name,
            phone_number,
            email
          ),
          collection_address:collection_address_id (
            street,
            street2,
            city,
            province,
            postal_code,
            country
          ),
          delivery_address:delivery_address_id (
            street,
            street2,
            city,
            province,
            postal_code,
            country
          )
        `)
        .eq('id', bookingId)
        .single();

      if (fetchError) throw fetchError;

      if (!data) {
        setError('Booking not found');
        return;
      }

      // Check access permissions
      const hasAccess = 
        isAdmin || 
        (data as any).user_id === user?.id ||
        user?.user_metadata?.tokenType === 'booking_access';

      if (!hasAccess) {
        setError('You do not have permission to view this booking');
        return;
      }

      // Fetch contacts
      const { data: contacts } = await supabase
        .from('booking_contacts')
        .select(`
          contact_type,
          is_primary,
          contact_book:contact_id (
            name,
            phone,
            email
          )
        `)
        .eq('booking_id', bookingId);

      const collectionContacts = contacts
        ?.filter((c: any) => c.contact_type === 'collection')
        .map((c: any) => ({
          name: c.contact_book?.name || '',
          phone: c.contact_book?.phone || '',
          email: c.contact_book?.email || ''
        })) || [];

      const deliveryContacts = contacts
        ?.filter((c: any) => c.contact_type === 'delivery')
        .map((c: any) => ({
          name: c.contact_book?.name || '',
          phone: c.contact_book?.phone || '',
          email: c.contact_book?.email || ''
        })) || [];

      // Fetch parcels
      const { data: parcelsData, error: parcelsError } = await supabase
        .from('booking_parcels')
        .select('id, parcel_type, description, weight, dimensions, value, quantity, other_type_specification, insurance_enabled')
        .eq('booking_id', bookingId);

      if (parcelsError) {
        console.error('Error fetching parcels:', parcelsError);
      }

      const formattedParcels = (parcelsData || []).map((p: any) => ({
        id: p.id,
        parcel_type: p.parcel_type || '',
        description: p.description,
        weight: p.weight,
        dimensions: p.dimensions,
        value: p.value,
        quantity: p.quantity || 1,
        other_type_specification: p.other_type_specification,
        insurance_enabled: p.insurance_enabled || false
      }));

      // Cast to any to work around TypeScript issues
      const bookingRaw = data as any;

      const bookingData: BookingDetailsType = {
        ...bookingRaw,
        collection_address: bookingRaw.collection_address || null,
        delivery_address: bookingRaw.delivery_address || null,
        collection_contacts: collectionContacts,
        delivery_contacts: deliveryContacts,
        parcels: formattedParcels,
        profiles: bookingRaw.profiles || null
      };

      setBooking(bookingData);
    } catch (err) {
      console.error('Error fetching booking:', err);
      setError('Failed to load booking details');
    } finally {
      setIsLoading(false);
    }
  }, [bookingId, rolesLoading, isAdmin, user?.id, user?.user_metadata?.tokenType]);

  useEffect(() => {
    fetchBooking();
  }, [bookingId, user?.id, isAdmin, rolesLoading, user?.user_metadata?.tokenType]);

  // Update page title
  useEffect(() => {
    if (booking) {
      document.title = `Booking ${booking.id.substring(0, 8)} - Details`;
    }
    return () => {
      document.title = 'Bookings';
    };
  }, [booking]);

  const onQuoteUpdate = async () => {
    if (booking) {
      console.log('[BOOKING-DETAILS-PAGE] Quote update triggered, invalidating quotes cache');
      quotesQuery.invalidateQuotes();
    }
  };

  const handleStatusUpdate = async () => {
    // Let realtime subscription handle updates
    console.log('[BOOKING-DETAILS-PAGE] Status update completed - relying on realtime');
  };

  const handleBookingUpdate = async (bookingId: string) => {
    console.log('[BOOKING-DETAILS-PAGE] Booking update requested:', bookingId);
    await fetchBooking();
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="container max-w-6xl py-4 md:py-8">
        <Card className="p-6">
          <div className="space-y-4">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-48" />
            <div className="grid gap-4 mt-6">
              <Skeleton className="h-32" />
              <Skeleton className="h-32" />
              <Skeleton className="h-32" />
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // Error state
  if (error || !booking) {
    return (
      <div className="container max-w-6xl py-4 md:py-8">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between">
            <span>{error || 'Booking not found'}</span>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate('/bookings')}
            >
              Back to Bookings
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const quotes = quotesQuery.data || [];

  return (
    <div className="container max-w-6xl py-2 md:py-4">
      <Card className="flex flex-col h-[calc(100vh-6rem)]">
        <div className="px-4 md:px-6 pt-4 md:pt-6 pb-4 border-b border-border bg-card/50 flex-shrink-0">
          <BookingDetailsHeader
            booking={booking}
            isAdmin={isAdmin}
            onStatusUpdate={handleStatusUpdate}
            showBackButton={true}
            isInModal={false}
          />
        </div>

        <div className="flex-1 overflow-hidden">
          <BookingDetailsTabs
            booking={booking}
            quotes={quotes}
            onQuoteUpdate={onQuoteUpdate}
            onBookingUpdate={handleBookingUpdate}
            isAdmin={isAdmin}
            isBookingOwner={isBookingOwner}
            defaultTab={defaultTab}
          />
        </div>
      </Card>
    </div>
  );
};

export default BookingDetails;
